/**
 * DecisionFlow — PlanLibraryIntentModal
 * ui/src/components/admin/PlanLibraryIntentModal.jsx
 *
 * S739 Step 8 — OI-T3B-REVIEW-AND-DEPLOY
 * Spec authority: comp_policies_spec_v2_14.md S31.4 (AD-200, AD-203)
 * Canon authority: decisionflow_library_design_canon.md Parts 3, 4
 *
 * Two-phase flow:
 *
 * Phase 1 — Collection (steps unchanged per L-115):
 *   confirm → assign → bu → motions → ote
 *   All six steps are UNCHANGED from prior implementation.
 *   These collect: template selection, role/segment, BU, selling motions, OTE range.
 *
 * Phase 2 — Review & Deploy (5-step full-screen overlay, replaces prior "intent" step):
 *   Step 1: Plan Design    — enriched snapshot preview, earning model, component weights
 *   Step 2: Economics      — OTE adjustable; pay mix ±10pp (AMBER); weights must sum 100%
 *   Step 3: Modifiers      — required pre-configured from modifier_defaults; optional grouped
 *   Step 4: Review Delta   — GREEN/AMBER/RED designation (AD-203), policy-sensitive disclaimer
 *   Step 5: Deploy         — [Deploy as Draft] or [Deploy & Submit] (conditional per AD-177)
 *
 * GREEN/AMBER/RED rules (AD-203, canon Part 3):
 *   GREEN — all parameters within certified envelope
 *   AMBER — any parameter at boundary, no structural change (pay mix ±10pp, weights ±10pp)
 *   RED   — any structural boundary crossed (earning model changed, required modifier removed,
 *            rate curve band count changed, component weight outside locked envelope)
 *   RED does NOT block deployment — it changes the certification_status label.
 *   CA-1 cannot elevate RED to GREEN by clicking through.
 *
 * certification_status passed to onConfirm and included in POST /template-library/deploy body.
 * template_delta.certification_status written by router (m_s738_01 added the column).
 *
 * Policy-sensitive disclaimer (locked copy from canon Part 3):
 *   "Parameters marked Policy-Sensitive reflect common design patterns and should be
 *    reviewed by your legal and HR teams before deployment. DecisionFlow does not
 *    provide legal or tax advice."
 *
 * Weight normalization: component weights must sum to 100% (AD-204).
 * Backend validates at deploy: 422 WEIGHT_NORMALIZATION_FAILED if violated.
 * UI enforces with linked adjustment — changing one weight adjusts others proportionally.
 *
 * onConfirm signature (unchanged — PlanLibrary.jsx compatibility):
 *   onConfirm(template, path, roleId, segmentId, sellingMotionIds,
 *             oteMin, oteMax, oteMidpoint, businessUnitCode, certificationStatus)
 *
 * Props:
 *   templates        — array of template objects (bulk or single)
 *   period           — { fiscal_period_id, period_code } | null
 *   onConfirm        — fn(template, path, roleId, segmentId, sellingMotionIds,
 *                         oteMin, oteMax, oteMidpoint, businessUnitCode, certificationStatus)
 *   onCancel         — fn()
 *   roles            — array from GET /api/v1/roles
 *   segments         — array from GET /api/v1/segments
 *   sellingMotions   — array from GET /api/v1/selling-motions
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind colors. Zero dark: variants.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Sparkles, AlertTriangle, Info, ChevronRight } from "lucide-react";

// ---------------------------------------------------------------------------
// Constants — locked from canon Part 3 (AD-203)
// ---------------------------------------------------------------------------

const POLICY_SENSITIVE_DISCLAIMER =
  "Parameters marked Policy-Sensitive reflect common design patterns and should be " +
  "reviewed by your legal and HR teams before deployment. DecisionFlow does not " +
  "provide legal or tax advice.";

// Pay mix AMBER boundary: ±10 percentage points from certified default (canon Part 3)
const PAY_MIX_AMBER_THRESHOLD_PP = 0.10;

// Component weight AMBER boundary: ±10pp from certified default (canon Part 3)
const WEIGHT_AMBER_THRESHOLD_PP = 0.10;

// Review & Deploy step keys
const RD_STEPS = [
  { key: "design",    label: "Plan Design"   },
  { key: "economics", label: "Economics"     },
  { key: "modifiers", label: "Modifiers"     },
  { key: "delta",     label: "Review Delta"  },
  { key: "deploy",    label: "Deploy"        },
];

// Earning model display labels (AD-201)
const EARNING_MODEL_LABELS = {
  arr_attainment:      "ARR Attainment",
  qualified_outcomes:  "Qualified Outcomes",
  retention_expansion: "Retention + Expansion",
  team_rollup:         "Team Rollup",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtPct(val) {
  if (val === null || val === undefined) return "—";
  return `${Math.round(Number(val) * 100)}%`;
}

function fmtMoney(val) {
  if (!val) return "—";
  return Number(val).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/**
 * Compute GREEN/AMBER/RED certification status from user customizations vs certified defaults.
 * Returns 'GREEN' | 'AMBER' | 'RED'.
 * RED if any structural boundary crossed; AMBER if any parameter at boundary; GREEN otherwise.
 */
function computeCertificationStatus({ certifiedPayMix, userPayMix, certifiedWeights, userWeights, requiredModifiersRemoved }) {
  // RED triggers (structural)
  if (requiredModifiersRemoved > 0) return "RED";

  // Weights structural check — any weight outside ±10pp ceiling = RED
  for (const key of Object.keys(certifiedWeights ?? {})) {
    const certified = certifiedWeights[key] ?? 0;
    const user = userWeights?.[key] ?? certified;
    if (Math.abs(user - certified) > WEIGHT_AMBER_THRESHOLD_PP + 0.001) return "RED";
  }

  // AMBER triggers (boundary)
  const payMixDelta = Math.abs((userPayMix ?? certifiedPayMix) - certifiedPayMix);
  if (payMixDelta > 0.001) {
    if (payMixDelta > PAY_MIX_AMBER_THRESHOLD_PP + 0.001) return "RED";
    return "AMBER";
  }

  // Weight AMBER check
  for (const key of Object.keys(certifiedWeights ?? {})) {
    const certified = certifiedWeights[key] ?? 0;
    const user = userWeights?.[key] ?? certified;
    if (Math.abs(user - certified) > 0.001) return "AMBER";
  }

  return "GREEN";
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-0">
      {steps.map((step, i) => {
        const isActive   = step.key === current;
        const isDone     = steps.findIndex((s) => s.key === current) > i;
        return (
          <div key={step.key} className="flex items-center">
            <div className={[
              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-tok-xs font-medium transition-colors",
              isActive  ? "bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]" :
              isDone    ? "text-[rgb(var(--color-success))]" :
                          "text-[rgb(var(--color-text-muted))]",
            ].join(" ")}>
              {isDone ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <span className={[
                  "h-4 w-4 rounded-full flex items-center justify-center text-[10px] shrink-0 border",
                  isActive ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
                           : "border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))]",
                ].join(" ")}>
                  {i + 1}
                </span>
              )}
              <span className="hidden sm:inline">{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <ChevronRight className="h-3 w-3 text-[rgb(var(--color-border))] mx-0.5 shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CertBadge({ status }) {
  const cfg = {
    GREEN: { label: "Certified Template",              cls: "bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]"   },
    AMBER: { label: "Materially Customized",           cls: "bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]"   },
    RED:   { label: "Custom Plan (derived)",           cls: "bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]"     },
  }[status] ?? { label: "—", cls: "" };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-tok-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function PolicySensitiveBanner() {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-warning))]/30
                    bg-[rgb(var(--color-warning))]/5 px-3 py-2.5">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
      <p className="text-[10px] text-[rgb(var(--color-warning))] leading-relaxed">
        {POLICY_SENSITIVE_DISCLAIMER}
      </p>
    </div>
  );
}

function SectionCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-lg border border-[rgb(var(--color-border))] px-4 py-3 ${className}`}>
      {title && (
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-2">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PlanLibraryIntentModal({
  templates,
  period,
  onConfirm,
  onCancel,
  roles = [],
  segments = [],
  sellingMotions = [],
}) {
  // ── Collection phase state (unchanged — L-115) ──
  const [step,             setStep]             = useState("confirm");
  const [queueIdx,         setQueueIdx]         = useState(0);
  const [path,             setPath]             = useState("plan_with_components");
  const [roleId,           setRoleId]           = useState("");
  const [segmentId,        setSegmentId]        = useState("");
  const [businessUnitCode, setBusinessUnitCode] = useState("");
  const [sellingMotionIds, setSellingMotionIds] = useState([]);
  const [oteMin,           setOteMin]           = useState("");
  const [oteMax,           setOteMax]           = useState("");
  const [oteError,         setOteError]         = useState(null);
  const [businessUnits,    setBusinessUnits]    = useState([]);
  const [buLoading,        setBuLoading]        = useState(false);
  const [buError,          setBuError]          = useState(null);

  // ── Review & Deploy phase state (new — T3-B) ──
  const [rdStep,           setRdStep]           = useState("design"); // design|economics|modifiers|delta|deploy
  const [userPayMixVar,    setUserPayMixVar]    = useState(null);     // null = use certified default
  const [userWeights,      setUserWeights]      = useState(null);     // null = use certified defaults
  const [weightError,      setWeightError]      = useState("");
  const [hasPolicySensitive, setHasPolicySensitive] = useState(false);

  const dialogRef  = useRef(null);
  const primaryRef = useRef(null);

  const isBulk          = templates.length > 1;
  const currentTemplate = templates[queueIdx];

  // Parse snapshot for Review & Deploy
  const snap = useMemo(() => {
    const gtm = currentTemplate?.gtm ?? {};
    return {
      earning_model:     gtm.earning_model        ?? currentTemplate?.earning_model ?? null,
      pay_mix_base:      gtm.pay_mix?.base        ?? currentTemplate?.component_defaults?.base_pct ?? 0.50,
      pay_mix_variable:  gtm.pay_mix?.variable    ?? currentTemplate?.component_defaults?.variable_pct ?? 0.50,
      component_weights: gtm.component_weights    ?? {
        arr:      currentTemplate?.component_defaults?.arr_weight      ?? 1.00,
        mbo:      currentTemplate?.component_defaults?.mbo_weight      ?? 0.00,
        nps:      currentTemplate?.component_defaults?.nps_weight      ?? 0.00,
        adoption: currentTemplate?.component_defaults?.adoption_weight ?? 0.00,
      },
      modifier_defaults: gtm.modifier_defaults    ?? {},
      ote_guidance:      gtm.ote_guidance          ?? null,
    };
  }, [currentTemplate]);

  // Effective pay mix variable (user override or certified default)
  const effectivePayMixVar = userPayMixVar ?? snap.pay_mix_variable;
  const effectiveWeights   = userWeights   ?? snap.component_weights;

  // OTE midpoint
  const oteMinNum  = parseFloat(oteMin);
  const oteMaxNum  = parseFloat(oteMax);
  const oteMidpoint = (!isNaN(oteMinNum) && !isNaN(oteMaxNum)) ? (oteMinNum + oteMaxNum) / 2 : null;

  // Certification status — computed from customizations
  const certificationStatus = useMemo(() => computeCertificationStatus({
    certifiedPayMix:         snap.pay_mix_variable,
    userPayMix:              userPayMixVar,
    certifiedWeights:        snap.component_weights,
    userWeights:             userWeights,
    requiredModifiersRemoved: 0, // Step 3 modifier removal tracked here in future
  }), [snap, userPayMixVar, userWeights]);

  // Policy-sensitive check — any POLICY_SENSITIVE modifier in defaults
  useEffect(() => {
    const hasSensitive = Boolean(snap.modifier_defaults?.["P3B-CLAWBACK"] || snap.modifier_defaults?.["P3B-DRAW"]);
    setHasPolicySensitive(hasSensitive);
  }, [snap.modifier_defaults]);

  // ── BU fetch (unchanged) ──
  useEffect(() => {
    let cancelled = false;
    setBuLoading(true);
    setBuError(null);
    fetch("/api/v1/business-units", { credentials: "include" })
      .then((res) => res.json())
      .then((json) => { if (!cancelled) setBusinessUnits((json.data ?? []).filter((b) => b.is_active)); })
      .catch(() => { if (!cancelled) setBuError("Could not load business units."); })
      .finally(() => { if (!cancelled) setBuLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Focus management ──
  useEffect(() => { primaryRef.current?.focus(); }, [step, rdStep, queueIdx]);

  // ── Escape key ──
  useEffect(() => {
    function handleKeyDown(e) { if (e.key === "Escape") onCancel(); }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  // ── Collection phase handlers (unchanged — L-115) ──
  function handleConfirmContinue() { setStep("assign"); }
  function handleAssignBack()      { setStep("confirm"); }
  function handleAssignContinue()  { setStep("bu"); }
  function handleBuBack()          { setStep("assign"); }
  function handleBuContinue()      { setStep("motions"); }
  function handleMotionsBack()     { setStep("bu"); }
  function handleMotionToggle(smId) {
    setSellingMotionIds((prev) =>
      prev.includes(smId) ? prev.filter((id) => id !== smId) : [...prev, smId]
    );
  }
  function handleMotionsContinue() { setStep("ote"); }
  function handleOteBack()         { setStep("motions"); }

  function handleOteContinue() {
    setOteError(null);
    const minRaw = oteMin.trim(), maxRaw = oteMax.trim();
    const bothBlank = minRaw === "" && maxRaw === "";
    const min = parseFloat(minRaw), max = parseFloat(maxRaw);
    if (!bothBlank) {
      if (minRaw === "" || isNaN(min)) { setOteError("Minimum OTE is required when Maximum OTE is provided."); return; }
      if (maxRaw === "" || isNaN(max)) { setOteError("Maximum OTE is required when Minimum OTE is provided."); return; }
      if (max <= min)                  { setOteError("Maximum OTE must be greater than Minimum OTE."); return; }
    }
    // Enter Review & Deploy phase — reset to Step 1
    setRdStep("design");
    setUserPayMixVar(null);
    setUserWeights(null);
    setWeightError("");
    setStep("rd");
  }

  function handleOteSkip() {
    setOteMin(""); setOteMax(""); setOteError(null);
    setRdStep("design");
    setUserPayMixVar(null);
    setUserWeights(null);
    setWeightError("");
    setStep("rd");
  }

  // ── Review & Deploy navigation ──
  function rdBack() {
    const idx = RD_STEPS.findIndex((s) => s.key === rdStep);
    if (idx === 0) { setStep("ote"); return; }
    setRdStep(RD_STEPS[idx - 1].key);
  }

  function rdNext() {
    if (rdStep === "economics") {
      // Validate weight sum before advancing
      const total = Object.values(effectiveWeights).reduce((a, b) => a + Number(b), 0);
      if (Math.abs(total - 1.0) > 0.005) {
        setWeightError(`Component weights must sum to 100%. Current total: ${Math.round(total * 100)}%.`);
        return;
      }
      setWeightError("");
    }
    const idx = RD_STEPS.findIndex((s) => s.key === rdStep);
    if (idx < RD_STEPS.length - 1) setRdStep(RD_STEPS[idx + 1].key);
  }

  // ── Pay mix adjustment (±10pp AMBER boundary) ──
  const handlePayMixChange = useCallback((variablePct) => {
    const v = Math.min(1.0, Math.max(0.0, parseFloat(variablePct) || 0));
    setUserPayMixVar(v);
  }, []);

  // ── Weight adjustment — linked: changing one key adjusts others proportionally ──
  const handleWeightChange = useCallback((key, rawVal) => {
    const val = Math.min(1.0, Math.max(0.0, parseFloat(rawVal) || 0));
    setUserWeights((prev) => {
      const base = prev ?? snap.component_weights;
      const others = Object.keys(base).filter((k) => k !== key);
      const currentOthersTotal = others.reduce((a, k) => a + Number(base[k]), 0);
      const remaining = Math.max(0, 1.0 - val);
      const next = { ...base, [key]: val };
      if (currentOthersTotal > 0.001) {
        const scale = remaining / currentOthersTotal;
        others.forEach((k) => { next[k] = Math.round(Number(base[k]) * scale * 1000) / 1000; });
      }
      return next;
    });
    setWeightError("");
  }, [snap.component_weights]);

  // ── Deploy confirm ──
  function handleDeploy(deployPath) {
    const min = parseFloat(oteMin) || null;
    const max = parseFloat(oteMax) || null;
    const mid = oteMidpoint;
    onConfirm(
      currentTemplate,
      deployPath,
      roleId    ? parseInt(roleId, 10)    : null,
      segmentId ? parseInt(segmentId, 10) : null,
      sellingMotionIds,
      min, max, mid,
      businessUnitCode || null,
      certificationStatus,  // S739 Step 8 — passed to PlanLibrary.jsx → deploy body
    );
    // Advance queue or let parent close
    if (queueIdx < templates.length - 1) {
      setQueueIdx((i) => i + 1);
      setStep("confirm");
      setRdStep("design");
      setPath("plan_with_components");
      setRoleId(""); setSegmentId(""); setBusinessUnitCode("");
      setSellingMotionIds([]);
      setOteMin(""); setOteMax(""); setOteError(null);
      setUserPayMixVar(null); setUserWeights(null); setWeightError("");
    }
  }

  // ── Shared button styles ──
  const btnPrimary   = "rounded-lg bg-[rgb(var(--color-primary))] px-5 py-2 text-tok-xs font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed";
  const btnSecondary = "rounded-lg border border-[rgb(var(--color-border))] px-4 py-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]";
  const inputCls     = "w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]";

  // ── Modal dimensions — collection phase vs full-screen overlay ──
  const isRdPhase = step === "rd";

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50"
      />

      {/* Dialog */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.15 }}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intent-modal-title"
        className={[
          "fixed z-50 bg-[rgb(var(--color-surface))] shadow-2xl overflow-hidden flex flex-col",
          "border border-[rgb(var(--color-border))]",
          isRdPhase
            // Full-screen overlay for Review & Deploy (spec S31.4)
            ? "inset-4 rounded-2xl sm:inset-8"
            // Centered modal for collection phase
            : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] max-w-[calc(100vw-2rem)] max-h-[90vh] rounded-2xl",
        ].join(" ")}
      >

        {/* ================================================================
            COLLECTION PHASE — Steps: confirm, assign, bu, motions, ote
            All unchanged from prior implementation per L-115.
            ================================================================ */}

        {step === "confirm" && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <h2 id="intent-modal-title" className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                {isBulk ? `Deploy ${templates.length} Packages` : "Deploy Package"}
              </h2>
              <button type="button" onClick={onCancel} aria-label="Close"
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-4 overflow-y-auto">
              {isBulk && (
                <div className="flex items-center gap-2">
                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Package {queueIdx + 1} of {templates.length}</span>
                  <div className="flex-1 h-1 rounded-full bg-[rgb(var(--color-border))]">
                    <div className="h-1 rounded-full bg-[rgb(var(--color-primary))] transition-all"
                         style={{ width: `${((queueIdx + 1) / templates.length) * 100}%` }} />
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-[rgb(var(--color-border))] divide-y divide-[rgb(var(--color-border))]">
                {isBulk ? templates.map((t, i) => (
                  <div key={t.template_id} className={`flex items-center gap-2 px-3 py-2.5 ${i < queueIdx ? "opacity-50" : ""}`}>
                    {i < queueIdx ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" /> :
                     i === queueIdx ? <Sparkles className="h-4 w-4 shrink-0 text-[rgb(var(--color-primary))]" /> :
                     <span className="h-4 w-4 shrink-0 rounded-full border border-[rgb(var(--color-border))]" />}
                    <span className={`text-tok-xs ${i === queueIdx ? "font-medium text-[rgb(var(--color-text))]" : "text-[rgb(var(--color-text-muted))]"}`}>
                      {t.template_name}
                    </span>
                    {t.platform_version && <span className="ml-auto text-[10px] text-[rgb(var(--color-text-muted))]">{t.platform_version}</span>}
                  </div>
                )) : (
                  <div className="px-4 py-3 space-y-1">
                    <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{currentTemplate.template_name}</p>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {[
                        currentTemplate.sales_motion?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                        currentTemplate.platform_version ? `data-fit Curated ${currentTemplate.platform_version}` : null,
                      ].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                  {isBulk
                    ? "Each package will create a separate draft plan for review and approval. Plans become active only after a second approver confirms them in the Approvals tab."
                    : "This will create a draft plan for review and approval. The plan becomes active only after a second approver confirms it in the Approvals tab."}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
              <button ref={primaryRef} type="button" onClick={handleConfirmContinue} className={btnPrimary}>Continue →</button>
            </div>
          </div>
        )}

        {step === "assign" && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <h2 id="intent-modal-title" className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Assign role and segment</h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{currentTemplate.template_name}</p>
              </div>
              <button type="button" onClick={onCancel} aria-label="Close"
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-4 overflow-y-auto">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                Select the role and segment this plan applies to. Curated packages are role-agnostic at the
                template level — you choose the scope at deploy time. The same package can be deployed
                multiple times for different role/segment combinations.
              </p>
              <div className="space-y-3">
                <label className="block">
                  <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Role</span>
                  <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={`${inputCls} mt-1`}>
                    <option value="">Select role (optional)</option>
                    {roles.filter((r) => r.is_active !== false).map((r) => (
                      <option key={r.role_id} value={r.role_id}>{r.role_name ?? r.role_code}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Segment</span>
                  <select value={segmentId} onChange={(e) => setSegmentId(e.target.value)} className={`${inputCls} mt-1`}>
                    <option value="">Select segment (optional)</option>
                    {segments.filter((s) => s.is_active !== false).map((s) => (
                      <option key={s.segment_id} value={s.segment_id}>{s.segment_name ?? s.segment_code}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <button type="button" onClick={handleAssignBack} className={btnSecondary}>← Back</button>
              <button ref={primaryRef} type="button" onClick={handleAssignContinue} className={btnPrimary}>Continue →</button>
            </div>
          </div>
        )}

        {step === "bu" && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <h2 id="intent-modal-title" className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Business unit</h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{currentTemplate.template_name}</p>
              </div>
              <button type="button" onClick={onCancel} aria-label="Close"
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-4 overflow-y-auto">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                Optionally scope this plan to a business unit. Leave blank if not applicable.
              </p>
              {buError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{buError}</p>}
              {buLoading ? (
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Loading business units…</p>
              ) : (
                <label className="block">
                  <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Business unit</span>
                  <select value={businessUnitCode} onChange={(e) => setBusinessUnitCode(e.target.value)} className={`${inputCls} mt-1`}>
                    <option value="">No business unit</option>
                    {businessUnits.map((b) => (
                      <option key={b.business_unit_id} value={b.bu_code}>{b.bu_name ?? b.bu_code}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <button type="button" onClick={handleBuBack} className={btnSecondary}>← Back</button>
              <button ref={primaryRef} type="button" onClick={handleBuContinue} className={btnPrimary}>Continue →</button>
            </div>
          </div>
        )}

        {step === "motions" && (
          <div className="flex flex-col overflow-hidden min-h-0">
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <h2 id="intent-modal-title" className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Assign selling motions</h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{currentTemplate.template_name}</p>
              </div>
              <button type="button" onClick={onCancel} aria-label="Close"
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-4 overflow-y-auto">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                Select selling motions for credit attribution. Optional — can be assigned later.
              </p>
              {sellingMotions.length === 0 ? (
                <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No selling motions configured for this tenant.</p>
                </div>
              ) : (
                <div className="rounded-lg border border-[rgb(var(--color-border))] divide-y divide-[rgb(var(--color-border))] max-h-60 overflow-y-auto">
                  {sellingMotions.map((sm) => (
                    <label key={sm.selling_motion_id}
                      className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-[rgb(var(--color-surface-muted))] transition-colors">
                      <input type="checkbox" checked={sellingMotionIds.includes(sm.selling_motion_id)}
                        onChange={() => handleMotionToggle(sm.selling_motion_id)}
                        className="shrink-0 accent-[rgb(var(--color-primary))]" />
                      <div className="min-w-0">
                        <p className="text-tok-xs font-medium text-[rgb(var(--color-text))]">{sm.motion_name}</p>
                        <p className="text-[10px] text-[rgb(var(--color-text-muted))]">{sm.motion_code}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <button type="button" onClick={handleMotionsBack} className={btnSecondary}>← Back</button>
              <button ref={primaryRef} type="button" onClick={handleMotionsContinue} className={btnPrimary}>Continue →</button>
            </div>
          </div>
        )}

        {step === "ote" && (
          <div className="flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <h2 id="intent-modal-title" className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">OTE Range — Budget Annotation (Optional)</h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{currentTemplate.template_name}</p>
              </div>
              <button type="button" onClick={onCancel} aria-label="Close"
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-4 overflow-y-auto">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                OTE targets for budget modelling only. Not used in payout calculation. Leave blank to skip.
              </p>
              {snap.ote_guidance && (
                <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-info))]/30 bg-[rgb(var(--color-info))]/5 px-3 py-2.5">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-info))]" />
                  <p className="text-[10px] text-[rgb(var(--color-info))]">
                    Curated guidance for this archetype: {fmtMoney(snap.ote_guidance.min)} – {fmtMoney(snap.ote_guidance.max)} · Midpoint {fmtMoney(snap.ote_guidance.midpoint)}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-tok-xs font-medium text-[rgb(var(--color-text))]">Minimum OTE</label>
                  <input type="number" step="1000" value={oteMin}
                    onChange={(e) => { setOteMin(e.target.value); setOteError(null); }}
                    placeholder="e.g. 180000 (optional)" className={inputCls} />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-tok-xs font-medium text-[rgb(var(--color-text))]">Maximum OTE</label>
                  <input type="number" step="1000" value={oteMax}
                    onChange={(e) => { setOteMax(e.target.value); setOteError(null); }}
                    placeholder="e.g. 240000 (optional)" className={inputCls} />
                </div>
              </div>
              <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 flex items-center justify-between">
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Midpoint (auto)</span>
                <span className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                  {oteMidpoint !== null ? fmtMoney(oteMidpoint) : "—"}
                </span>
              </div>
              {oteError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{oteError}</p>}
            </div>
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <button type="button" onClick={handleOteBack} className={btnSecondary}>← Back</button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleOteSkip} className={btnSecondary}>Skip OTE</button>
                <button ref={primaryRef} type="button" onClick={handleOteContinue} className={btnPrimary}>Review & Deploy →</button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================
            REVIEW & DEPLOY PHASE — 5-step full-screen overlay (S31.4 / T3-B)
            ================================================================ */}

        {step === "rd" && (
          <div className="flex flex-col h-full overflow-hidden">

            {/* Header — template name + step indicator */}
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-3 shrink-0 gap-4">
              <div className="min-w-0">
                <h2 id="intent-modal-title" className="text-tok-sm font-semibold text-[rgb(var(--color-text))] truncate">
                  {currentTemplate.template_name}
                </h2>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Review &amp; Deploy</p>
              </div>
              <StepIndicator steps={RD_STEPS} current={rdStep} />
              <button type="button" onClick={onCancel} aria-label="Close"
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body — step content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">

              {/* ── Step 1: Plan Design ── */}
              {rdStep === "design" && (
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div>
                    <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">What you're getting</h3>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                      Enriched snapshot from this curated package.
                    </p>
                  </div>

                  <SectionCard title="Earning Model">
                    <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                      {EARNING_MODEL_LABELS[snap.earning_model] ?? snap.earning_model ?? "Standard ARR Attainment"}
                    </p>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">
                      {snap.earning_model === "arr_attainment"      && "Quota attainment against a rate curve. Variable pay tied directly to ARR production."}
                      {snap.earning_model === "qualified_outcomes"  && "Qualified opportunities and accepted pipeline. Paid on outcomes directly created."}
                      {snap.earning_model === "retention_expansion" && "GRR + NRR expansion against separate curves. Retention first, growth second."}
                      {snap.earning_model === "team_rollup"         && "Team attainment aggregated from individual sellers. Manager accountability through the team."}
                    </p>
                  </SectionCard>

                  <SectionCard title="Pay Mix">
                    <div className="flex items-center justify-between">
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Base</span>
                      <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmtPct(snap.pay_mix_base)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Variable</span>
                      <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmtPct(snap.pay_mix_variable)}</span>
                    </div>
                  </SectionCard>

                  <SectionCard title="Component Weights">
                    <div className="space-y-1.5">
                      {Object.entries(snap.component_weights).filter(([, v]) => v > 0).map(([key, val]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-tok-xs text-[rgb(var(--color-text-muted))] capitalize">{key.replace(/_/g, " ")}</span>
                          <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">{fmtPct(val)}</span>
                        </div>
                      ))}
                    </div>
                  </SectionCard>

                  {Object.keys(snap.modifier_defaults).length > 0 && (
                    <SectionCard title="Pre-configured Modifiers">
                      <div className="space-y-2">
                        {Object.entries(snap.modifier_defaults).map(([modType, values]) => (
                          <div key={modType} className="flex items-start justify-between gap-2">
                            <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">{modType}</span>
                            <span className="text-tok-xs text-[rgb(var(--color-text-muted))] text-right">
                              {modType === "P3B-REVTYPE"  && `New ${values.new_logo_factor}× · Exp ${values.expansion_factor}× · Ren ${values.renewal_factor}×`}
                              {modType === "P3B-CLAWBACK" && `${values.recovery_pct}% recovery · ${values.clawback_window_days}d window`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </SectionCard>
                  )}
                </div>
              )}

              {/* ── Step 2: Economics ── */}
              {rdStep === "economics" && (
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div>
                    <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Economics</h3>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                      Adjust within the certified envelope. Changes outside ±10pp are flagged AMBER or RED.
                    </p>
                  </div>

                  {/* OTE — read-only summary from collection phase */}
                  <SectionCard title="OTE Range">
                    {(oteMin || oteMax) ? (
                      <div className="flex items-center justify-between">
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Min / Midpoint / Max</span>
                        <span className="text-tok-xs font-medium text-[rgb(var(--color-text))]">
                          {fmtMoney(parseFloat(oteMin))} · {fmtMoney(oteMidpoint)} · {fmtMoney(parseFloat(oteMax))}
                        </span>
                      </div>
                    ) : (
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">Not set — skipped in OTE step.</p>
                    )}
                  </SectionCard>

                  {/* Pay mix adjustment — ±10pp AMBER boundary */}
                  <SectionCard title="Variable Pay Mix">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-tok-xs">
                        <span className="text-[rgb(var(--color-text-muted))]">Certified default</span>
                        <span className="font-medium text-[rgb(var(--color-text))]">{fmtPct(snap.pay_mix_variable)} variable</span>
                      </div>
                      <input type="range" min="0" max="100" step="1"
                        value={Math.round(effectivePayMixVar * 100)}
                        onChange={(e) => handlePayMixChange(parseInt(e.target.value, 10) / 100)}
                        className="w-full accent-[rgb(var(--color-primary))]"
                      />
                      <div className="flex items-center justify-between text-tok-xs">
                        <span className="text-[rgb(var(--color-text-muted))]">Your selection</span>
                        <span className={`font-semibold ${Math.abs(effectivePayMixVar - snap.pay_mix_variable) > PAY_MIX_AMBER_THRESHOLD_PP + 0.001
                          ? "text-[rgb(var(--color-danger))]"
                          : Math.abs(effectivePayMixVar - snap.pay_mix_variable) > 0.001
                          ? "text-[rgb(var(--color-warning))]"
                          : "text-[rgb(var(--color-text))]"}`}>
                          {fmtPct(effectivePayMixVar)} variable / {fmtPct(1 - effectivePayMixVar)} base
                        </span>
                      </div>
                    </div>
                  </SectionCard>

                  {/* Component weights — linked, must sum 100% */}
                  <SectionCard title="Component Weights">
                    <div className="space-y-3">
                      {Object.entries(snap.component_weights).map(([key, certVal]) => {
                        const userVal = effectiveWeights[key] ?? certVal;
                        const delta = userVal - certVal;
                        return (
                          <div key={key} className="space-y-1">
                            <div className="flex items-center justify-between text-tok-xs">
                              <span className="text-[rgb(var(--color-text))] capitalize font-medium">{key.replace(/_/g, " ")}</span>
                              <span className={delta > 0.001 ? "text-[rgb(var(--color-warning))]" : delta < -0.001 ? "text-[rgb(var(--color-warning))]" : "text-[rgb(var(--color-text-muted))]"}>
                                {fmtPct(userVal)} {Math.abs(delta) > 0.001 ? `(${delta > 0 ? "+" : ""}${Math.round(delta * 100)}pp)` : ""}
                              </span>
                            </div>
                            <input type="range" min="0" max="100" step="1"
                              value={Math.round(userVal * 100)}
                              onChange={(e) => handleWeightChange(key, parseInt(e.target.value, 10) / 100)}
                              className="w-full accent-[rgb(var(--color-primary))]"
                            />
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between text-tok-xs border-t border-[rgb(var(--color-border))] pt-2 mt-1">
                        <span className="text-[rgb(var(--color-text-muted))]">Total</span>
                        <span className={`font-semibold ${Math.abs(Object.values(effectiveWeights).reduce((a, b) => a + Number(b), 0) - 1.0) > 0.005
                          ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
                          {Math.round(Object.values(effectiveWeights).reduce((a, b) => a + Number(b), 0) * 100)}%
                        </span>
                      </div>
                    </div>
                    {weightError && <p className="text-tok-xs text-[rgb(var(--color-danger))] mt-2">{weightError}</p>}
                  </SectionCard>
                </div>
              )}

              {/* ── Step 3: Modifiers ── */}
              {rdStep === "modifiers" && (
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div>
                    <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Modifiers</h3>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                      Required modifiers are pre-configured from curated defaults. Optional modifiers can be added after deployment.
                    </p>
                  </div>

                  {Object.keys(snap.modifier_defaults).length > 0 ? (
                    <SectionCard title="Required — Pre-configured">
                      <div className="space-y-3">
                        {Object.entries(snap.modifier_defaults).map(([modType, values]) => (
                          <div key={modType} className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">{modType}</span>
                              <span className="text-[10px] rounded-full px-2 py-0.5 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]">Required</span>
                            </div>
                            <div className="mt-1.5 space-y-1">
                              {Object.entries(values).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between text-[10px]">
                                  <span className="text-[rgb(var(--color-text-muted))]">{k.replace(/_/g, " ")}</span>
                                  <span className="text-[rgb(var(--color-text))] font-medium">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                            {/* Policy-sensitive flag for clawback and draw */}
                            {(modType === "P3B-CLAWBACK" || modType === "P3B-DRAW") && (
                              <p className="mt-1.5 text-[10px] text-[rgb(var(--color-warning))]">⚠ Policy-Sensitive</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </SectionCard>
                  ) : (
                    <SectionCard>
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                        No modifier defaults defined for this curated package.
                        Required modifiers will be available for configuration after deployment.
                      </p>
                    </SectionCard>
                  )}

                  {hasPolicySensitive && <PolicySensitiveBanner />}

                  <SectionCard title="Optional — Available after deployment">
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Optional modifiers (kicker, SPIFF, draw, overlay) can be added via the Plan Builder
                      after this plan is deployed and approved.
                    </p>
                  </SectionCard>
                </div>
              )}

              {/* ── Step 4: Review Delta ── */}
              {rdStep === "delta" && (
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div>
                    <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Review Delta</h3>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                      What you changed from the curated defaults.
                    </p>
                  </div>

                  {/* Certification status badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Designation after deploy</span>
                    <CertBadge status={certificationStatus} />
                  </div>

                  {/* RED warning */}
                  {certificationStatus === "RED" && (
                    <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-danger))]/30
                                    bg-[rgb(var(--color-danger))]/5 px-3 py-2.5">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-danger))]" />
                      <p className="text-[10px] text-[rgb(var(--color-danger))] leading-relaxed">
                        One or more structural boundaries have been crossed. This plan will be designated
                        <strong> Custom Plan (derived)</strong> rather than a certified template.
                        This cannot be elevated to certified status — it is not blocked, but the designation is final.
                      </p>
                    </div>
                  )}

                  {/* Delta summary */}
                  {(() => {
                    const changes = [];
                    const payDelta = (userPayMixVar ?? snap.pay_mix_variable) - snap.pay_mix_variable;
                    if (Math.abs(payDelta) > 0.001) {
                      changes.push({ label: "Variable pay mix", from: fmtPct(snap.pay_mix_variable), to: fmtPct(userPayMixVar) });
                    }
                    if (userWeights) {
                      Object.entries(snap.component_weights).forEach(([key, certVal]) => {
                        const userVal = userWeights[key] ?? certVal;
                        if (Math.abs(userVal - certVal) > 0.001) {
                          changes.push({ label: `${key.replace(/_/g, " ")} weight`, from: fmtPct(certVal), to: fmtPct(userVal) });
                        }
                      });
                    }
                    if (oteMin || oteMax) {
                      changes.push({ label: "OTE range", from: "Not set", to: `${fmtMoney(parseFloat(oteMin))} – ${fmtMoney(parseFloat(oteMax))}` });
                    }
                    return changes.length > 0 ? (
                      <SectionCard title="Your changes">
                        <div className="space-y-2">
                          {changes.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-tok-xs">
                              <span className="text-[rgb(var(--color-text-muted))]">{c.label}</span>
                              <span className="text-[rgb(var(--color-text))]">
                                <span className="line-through text-[rgb(var(--color-text-muted))] mr-1.5">{c.from}</span>
                                {c.to}
                              </span>
                            </div>
                          ))}
                        </div>
                      </SectionCard>
                    ) : (
                      <SectionCard>
                        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">
                          No changes from curated defaults. This plan will deploy as-certified.
                        </p>
                      </SectionCard>
                    );
                  })()}

                  {hasPolicySensitive && <PolicySensitiveBanner />}
                </div>
              )}

              {/* ── Step 5: Deploy ── */}
              {rdStep === "deploy" && (
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div>
                    <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Deploy</h3>
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                      Confirm your selections and deploy.
                    </p>
                  </div>

                  {/* Summary card */}
                  <SectionCard title="Deployment summary">
                    <div className="space-y-2 text-tok-xs">
                      <div className="flex justify-between">
                        <span className="text-[rgb(var(--color-text-muted))]">Package</span>
                        <span className="font-medium text-[rgb(var(--color-text))]">{currentTemplate.template_name}</span>
                      </div>
                      {roleId && <div className="flex justify-between">
                        <span className="text-[rgb(var(--color-text-muted))]">Role</span>
                        <span className="text-[rgb(var(--color-text))]">{roles.find((r) => String(r.role_id) === roleId)?.role_name ?? roleId}</span>
                      </div>}
                      {segmentId && <div className="flex justify-between">
                        <span className="text-[rgb(var(--color-text-muted))]">Segment</span>
                        <span className="text-[rgb(var(--color-text))]">{segments.find((s) => String(s.segment_id) === segmentId)?.segment_name ?? segmentId}</span>
                      </div>}
                      {(oteMin || oteMax) && <div className="flex justify-between">
                        <span className="text-[rgb(var(--color-text-muted))]">OTE</span>
                        <span className="text-[rgb(var(--color-text))]">{fmtMoney(parseFloat(oteMin))} – {fmtMoney(parseFloat(oteMax))}</span>
                      </div>}
                      <div className="flex justify-between pt-1 border-t border-[rgb(var(--color-border))]">
                        <span className="text-[rgb(var(--color-text-muted))]">Designation</span>
                        <CertBadge status={certificationStatus} />
                      </div>
                    </div>
                  </SectionCard>

                  {/* Deploy path selection */}
                  <div className="space-y-2">
                    <p className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Deploy as</p>
                    {[
                      { value: "plan_with_components", label: "Plan with components",
                        sublabel: "Pre-configured from curated defaults. Recommended." },
                      { value: "plan_only", label: "Plan only",
                        sublabel: "Shell plan only. Configure components separately." },
                    ].map((opt) => (
                      <label key={opt.value} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors
                        ${path === opt.value
                          ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                          : "border-[rgb(var(--color-border))] hover:border-[rgb(var(--color-primary))]/50"}`}>
                        <input type="radio" name="deploy-path" value={opt.value}
                               checked={path === opt.value} onChange={() => setPath(opt.value)}
                               className="mt-0.5 shrink-0 accent-[rgb(var(--color-primary))]" />
                        <div>
                          <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">{opt.label}</p>
                          <p className="text-[10px] text-[rgb(var(--color-text-muted))] mt-0.5">{opt.sublabel}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-info))]/30
                                  bg-[rgb(var(--color-info))]/5 px-3 py-2.5">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-info))]" />
                    <p className="text-[10px] text-[rgb(var(--color-info))] leading-relaxed">
                      <strong>Deploy as Draft</strong> creates the plan for configuration and review.{" "}
                      <strong>Deploy &amp; Submit</strong> deploys and immediately submits for CA-2 approval —
                      only when no required modifiers need configuration.
                    </p>
                  </div>
                </div>
              )}

            </div>

            {/* Footer — navigation */}
            <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-6 py-4 shrink-0">
              <button type="button" onClick={rdBack} className={btnSecondary}>← Back</button>

              {rdStep !== "deploy" ? (
                <button ref={primaryRef} type="button" onClick={rdNext} className={btnPrimary}>
                  Continue →
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button type="button"
                    onClick={() => handleDeploy("plan_with_components")}
                    className={btnSecondary}>
                    Deploy as Draft
                  </button>
                  <button ref={primaryRef} type="button"
                    onClick={() => handleDeploy(path)}
                    className={btnPrimary}>
                    Deploy &amp; Submit for Approval →
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

      </motion.div>
    </>
  );
}
